import { createStore, applyMiddleware, combineReducers } from "redux";
import createSagaMiddleware from "redux-saga";
import thunkMiddleware from "redux-thunk";
import { createBrowserHistory } from "history";
import {
  routerReducer,
  routerMiddleware as createRouterMiddleware,
} from "react-router-redux";

import tesseractReducer from "tesseract/src/reducers";
import authReducer, { authWatcher } from "apm/modules/auth";
import dropdownReducer from "apm/modules/dropdowns";
import focusSelectorReducer from "apm/modules/focus-selector";
import chartCursorReducer from "apm/modules/chart-cursor";
import organizationReducer, {
  organizationWatcher,
} from "apm/modules/organizations";
import paginateReducer from "apm/modules/paginate";
import selectedTraceReducer, {
  selectedTraceWatcher,
} from "apm/modules/selected-trace";
import serviceTransactionsReducer, {
  serviceTransactionsWatcher,
} from "apm/modules/service-transactions";
import sortsReducer from "apm/modules/sorts";
import timeSelectorReducer from "apm/modules/time-selector";
import traceCountsReducer, {
  traceCountsWatcher,
} from "apm/modules/trace-counts";
import traceHistogramsReducer, {
  traceHistogramsWatcher,
} from "apm/modules/trace-histograms";
import tracesReducer, { tracesWatcher } from "apm/modules/traces";
import windowReducer from "apm/modules/window";
import sidebarReducer from "apm/modules/sidebar";
import wizardReducer from "components/Wizard/modules/wizard";
import alertDetailsReducer from "alerts/modules/alert-details";
import flashMessageReducer from "modules/flash-message";

import onboardingStepReducer from "onboarding/modules/onboarding-step";
import hostOnboardingReducer from "onboarding/modules/host-onboarding";
import apmOnboardingReducer from "onboarding/modules/apm-onboarding";
import awsOnboardingReducer from "onboarding/modules/aws-onboarding";
import environmentReducer from "onboarding/reducers/environment-reducer";
import tokenReducer from "onboarding/reducers/token-reducer";
import tabReducer from "onboarding/reducers/tab-reducer";

import { setupIntercomNotifier } from "utils/intercom";

import usageReducer from "organization/modules/usage";
import savedViewReducer from "infrastructure/modules/savedViewReducer";
import customThresholdsReducer from "infrastructure/modules/customThresholdsReducer";
import hostViewReducer from "infrastructure/HostView/reducers/host-view-reducer";
import hostViewCursorReducer from "infrastructure/HostView/reducers/host-view-cursor-reducer";
import signUpReducer, { signUpWatcher } from "auth/modules/sign-up";

export const history = createBrowserHistory();
setupIntercomNotifier(history);

const sagaMiddleware = createSagaMiddleware();
const routerMiddleware = createRouterMiddleware(history);

export const createStoreWithMiddleware = applyMiddleware(
  sagaMiddleware,
  thunkMiddleware,
  routerMiddleware
)(createStore);

function* rootSaga() {
  yield [
    authWatcher(),
    organizationWatcher(),
    selectedTraceWatcher(),
    serviceTransactionsWatcher(),
    signUpWatcher(),
    traceCountsWatcher(),
    traceHistogramsWatcher(),
    tracesWatcher(),
  ];
}

const RootReducer = combineReducers({
  alert: combineReducers({
    details: alertDetailsReducer,
  }),
  flashMessage: flashMessageReducer,
  auth: authReducer,
  usage: usageReducer,
  entities: combineReducers({
    organizations: organizationReducer,
    selectedTrace: selectedTraceReducer,
    traceCounts: traceCountsReducer,
    traces: tracesReducer,
    transactions: serviceTransactionsReducer,
  }),
  onboarding: combineReducers({
    step: onboardingStepReducer,
    apm: apmOnboardingReducer,
    aws: awsOnboardingReducer,
    host: hostOnboardingReducer,
    environment: environmentReducer,
    token: tokenReducer,
    tab: tabReducer,
  }),
  pagination: paginateReducer,
  series: combineReducers({
    trace: traceHistogramsReducer,
  }),
  signUp: signUpReducer,
  routing: routerReducer,
  tesseract: tesseractReducer,
  infrastructure: combineReducers({
    hostView: hostViewReducer,
    cursor: hostViewCursorReducer,
  }),
  ui: combineReducers({
    chartCursor: chartCursorReducer,
    dropdowns: dropdownReducer,
    focusSelector: focusSelectorReducer,
    sorts: sortsReducer,
    timeSelector: timeSelectorReducer,
    window: windowReducer,
    infrastructureSavedView: savedViewReducer,
    customThresholds: customThresholdsReducer,
    wizard: wizardReducer,
    sidebar: sidebarReducer,
  }),
});

export function createStoreFromState(initialState = {}, devtools = undefined) {
  const store = createStoreWithMiddleware(
    RootReducer,
    initialState,
    devtools ? devtools() : f => f
  );
  sagaMiddleware.run(rootSaga);
  return store;
}
